import Timeline from '@mui/lab/Timeline'
import TimelineConnector, { timelineConnectorClasses } from '@mui/lab/TimelineConnector'
import TimelineContent, { timelineContentClasses } from '@mui/lab/TimelineContent'
import TimelineDot, { timelineDotClasses } from '@mui/lab/TimelineDot'
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { styled, type Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import theme from 'assets/theme'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { compactZonedDate } from 'global/helpers/dates'
import i18n from 'i18n'
import { type ReactElement, useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { getTypeIcon } from 'screens/TripDetails/TripDetails.helpers'
import { getStatusId } from 'screens/TripDetails/TripDetails.service'
import type { GeofenceTripLogData, LegData, StatusTripLogData, TripLogData } from './TripDetails.types'

export const TripTimeline = styled(Timeline)(({ theme }: { theme: Theme }) => ({
  '&': {
    padding: 0,
  },
  [`& .${timelineItemClasses.root}`]: {
    minHeight: 'auto',
    '&:before': {
      flex: 0,
      padding: 0,
    },
  },
  [`& .${timelineDotClasses.root}`]: {
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
    marginTop: '4px',
    marginBottom: '4px',
  },
  [`& .${timelineConnectorClasses.root}`]: {
    backgroundColor: theme.palette.primary.main,
  },
  [`& .${timelineContentClasses.root}`]: {
    padding: '4px 8px',
    margin: '-2px 8px 8px 8px',
    borderRadius: '4px',
    '& strong': {
      color: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
}))

const getTripLogLabel = (tripLog: TripLogData): string | ReactElement => {
  switch (tripLog.type) {
    case 'ARRIVED_AT_LOCATION':
    case 'DEPARTED_FROM_LOCATION': {
      const geofenceData: GeofenceTripLogData = tripLog.data as GeofenceTripLogData
      return <Trans
        i18nKey={`screens.cs.trip_details.events.${tripLog.type}`}
        values={{
          location: geofenceData.name,
        }}
        components={{ strong: <strong /> }}
      />
    }
    case 'STATUS_WITHOUT_WAYPOINT_UPDATED':
    case 'STATUS_UPDATED': {
      const statusData: StatusTripLogData = tripLog.data as StatusTripLogData
      const statusId = getStatusId(statusData.statusCode, statusData.reasonCode)
      return <Trans
        i18nKey={`screens.cs.trip_details.events.${tripLog.type}`}
        values={{
          shipmentNumber: statusData.shipmentNumber,
          status: i18n.t(`waypoints.statuses.${statusId}`),
        }}
        components={{ strong: <strong /> }}
      />
    }
    default:
      return i18n.t(`screens.cs.trip_details.events.${tripLog.type}`)
  }
}

const PickupIcon = getTypeIcon('PUP')
const DeliveryIcon = getTypeIcon('DEL')
const CustomsIcon = getTypeIcon('CUS')

export interface TripLogsProps {
  tripLogs: TripLogData[]
  legs: LegData[]
  stopsViewAvailable?: boolean
}

const TripLogs = ({ tripLogs, legs, stopsViewAvailable }: TripLogsProps): ReactElement | null => {
  const { t } = useTranslation()
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)

  const handleTimelineClick = (tripLog: TripLogData): void => {
    switch (tripLog.type) {
      case 'ARRIVED_AT_LOCATION':
      case 'DEPARTED_FROM_LOCATION':
      case 'STATUS_UPDATED': {
        tripDetailsDispatch({
          type: 'setDetailsView',
          payload: stopsViewAvailable ? 'sequence_map' : 'locations_map',
        })
        if (tripLog.data?.geoPoint) {
          tripDetailsDispatch({
            type: 'setFocusGeoPoint',
            payload: tripDetailsState.focusGeoPoint !== tripLog.data.geoPoint ? tripLog.data.geoPoint : undefined,
          })
        }
        break
      }
      default:
        break
    }
  }

  const getTripIcon = (tripLog: TripLogData): ReactElement => {
    const { wayPointCid } = tripLog.data ?? {}
    const iconProps = { sx: { width: '1.25rem', height: '1.25rem', fill: theme.palette.primary.main } }
    for (const leg of legs) {
      for (const waypoint of leg.wayPoints) {
        if (waypoint.wayPointCid === wayPointCid) {
          switch (waypoint.type) {
            case 'PUP': {
              return <PickupIcon {...iconProps} />
            }
            case 'DEL': {
              return <DeliveryIcon {...iconProps} />
            }
            case 'CUS': {
              return <CustomsIcon {...iconProps} />
            }
            default: {
              break
            }
          }
        }
      }
    }
    return <TimelineDot {...iconProps} />
  }

  return tripLogs.length === 0 ? (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
      <Typography variant="p3" color="primary.light" py={2}>
        {t('screens.cs.trip_details.sidebar.no_milestones')}
      </Typography>
    </Stack>
  ) : (
    <Box sx={{ padding: 2, overflow: 'auto' }}>
      <TripTimeline>
        {tripLogs.map((log, index) => (
          <TimelineItem key={log.entityId}>
            <TimelineSeparator>
              {getTripIcon(log)}
              {(index !== tripLogs.length - 1) && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent onClick={(): void => handleTimelineClick(log)}>
              <Typography variant="p6" color="primary.light" component="p">
                {compactZonedDate(log.createdAt, log.data?.offset)}
              </Typography>
              <Typography color="text.main" variant="p3">
                {getTripLogLabel(log)}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        ))}
      </TripTimeline>
    </Box>
  )
}

export default TripLogs
